import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { ConfirmSignUpForm } from '../components/forms/ConfirmSignUpForm';
import apiClient from '../../../../shared/lib/apiClient';
import { ValidationService, getPasswordStrength } from '../../../../shared/lib/validation';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'signIn' | 'signUp' | 'confirm'>('signIn');

  // Sign-up state
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const [signUpMessage, setSignUpMessage] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  // Password strength for sign-up password
  const signupPasswordStrength = getPasswordStrength(signupPassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await signIn(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSignUpError(null);
    setSignUpMessage(null);

    try {
      // Basic validation
      if (signupPassword !== signupConfirmPassword) {
        setSignUpError('Passwords do not match');
        return;
      }

      const validation = ValidationService.validateSignUp({ email: signupEmail, password: signupPassword });
      if (!validation.isValid) {
        setSignUpError(validation.errors.map(e => e.message).join('; '));
        return;
      }

      await apiClient('/auth/signup', {
        method: 'POST',
        body: { email: signupEmail, password: signupPassword },
        requireAuth: false
      });

      setSignUpMessage('Signup successful. Please check your email for a confirmation code.');
      setPendingEmail(signupEmail);
      setViewMode('confirm');
    } catch (err: any) {
      setSignUpError(err.message || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (email: string, confirmationCode: string) => {
    setIsLoading(true);
    try {
      await apiClient('/auth/confirm-signup', {
        method: 'POST',
        body: { email, confirmation_code: confirmationCode },
        requireAuth: false
      });

      // After successful confirmation, return to sign-in
      setSignUpMessage('Account confirmed. You may now sign in.');
      setPendingEmail(null);
      setViewMode('signIn');
    } catch (err: any) {
      // Propagate error so ConfirmSignUpForm can show it via its own handler
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-white">
          Welcome Back
        </h2>
        <p className="mt-2 text-sm text-gray-400">
          Sign in to your account
        </p>
      </div>

      {/** Sign-in form */}
      {viewMode === 'signIn' && (
        <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
            Email address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            className="block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            className="block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Signing in...
            </div>
          ) : (
            'Sign In'
          )}
        </button>
        </form>
      )}

      {/** Sign-up form */}
      {viewMode === 'signUp' && (
        <form onSubmit={handleSignUp} className="space-y-6">
          {signUpError && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-md text-sm">
              {signUpError}
            </div>
          )}

          {signUpMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
              {signUpMessage}
            </div>
          )}

          <div>
            <label htmlFor="signupEmail" className="block text-sm font-medium text-gray-300 mb-2">
              Email address
            </label>
            <input
              id="signupEmail"
              type="email"
              value={signupEmail}
              onChange={(e) => setSignupEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200"
            />
          </div>

          <div>
            <label htmlFor="signupPassword" className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              id="signupPassword"
              type="password"
              value={signupPassword}
              onChange={(e) => setSignupPassword(e.target.value)}
              placeholder="Create a password"
              required
              className="block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200"
            />
            {/* Password strength indicator */}
            <div className="mt-2">
              <div className="h-2 w-full bg-gray-600 rounded">
                <div
                  className={`h-2 rounded ${signupPasswordStrength.color === 'red' ? 'bg-red-500' : signupPasswordStrength.color === 'yellow' ? 'bg-yellow-400' : signupPasswordStrength.color === 'blue' ? 'bg-blue-500' : signupPasswordStrength.color === 'green' ? 'bg-green-500' : 'bg-gray-400'}`}
                  style={{ width: `${Math.min(100, Math.round((signupPasswordStrength.score / 6) * 100))}%` }}
                />
              </div>
              <div className="text-xs mt-1 text-gray-300">{signupPasswordStrength.label}</div>
            </div>
          </div>

          <div>
            <label htmlFor="signupConfirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
              Confirm Password
            </label>
            <input
              id="signupConfirmPassword"
              type="password"
              value={signupConfirmPassword}
              onChange={(e) => setSignupConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              className="block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-600 to-teal-500 hover:from-green-700 hover:to-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing up...' : 'Create account'}
          </button>

          <div className="mt-4 text-center">
            <button type="button" className="text-sm text-gray-400 hover:underline" onClick={() => setViewMode('signIn')}>
              Back to sign in
            </button>
          </div>
        </form>
      )}

      {/** Confirmation step rendered after signup */}
      {viewMode === 'confirm' && pendingEmail && (
        <ConfirmSignUpForm
          email={pendingEmail}
          onConfirm={handleConfirm}
          onBack={() => setViewMode('signIn')}
        />
      )}

      <div className="mt-6 text-center">
        {viewMode === 'signIn' ? (
          <p className="text-gray-400 text-sm">
            Don't have an account? <button className="text-primary hover:underline ml-1" onClick={() => setViewMode('signUp')}>Sign up</button>
          </p>
        ) : null}
      </div>
    </>
  );
};

export default LoginPage;